/*
  Picking the categories a plan covers.

  What was here before: one multi-select holding all 227 categories in a single
  flat alphabetical-ish list, with no indication of which section a name belonged
  to. "Car Seats" and "Cars For Sale" sit next to each other and read the same;
  the only way to pick a whole section was to find each of its children by eye
  and click them one at a time.

  This groups them under the section they belong to, so a whole section is one
  click, and it says out loud what is selected.
*/
import React, { useMemo, useState } from "react";

const box = {
  border: "1px solid #ced4da",
  borderRadius: "6px",
  background: "#fff",
};

const CategoryPicker = ({ categories = [], value = [], onChange }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState({});

  const selected = useMemo(() => new Set((value || []).map(Number)), [value]);

  /* Every category filed under the top-level section it ultimately belongs to.
     The tree is only two deep today, but walking to the root costs nothing and
     will not break the day somebody adds a third level from Categories
     Management. */
  const { groups, byId } = useMemo(() => {
    const byId = new Map(categories.map((c) => [Number(c.id), c]));
    const rootOf = (c) => {
      let cur = c;
      let guard = 0;
      while (cur && Number(cur.parent_id) && byId.has(Number(cur.parent_id)) && guard++ < 12) {
        cur = byId.get(Number(cur.parent_id));
      }
      return cur;
    };
    const map = new Map();
    for (const c of categories) {
      const root = rootOf(c) || c;
      const key = Number(root.id);
      if (!map.has(key)) map.set(key, { root, items: [] });
      map.get(key).items.push(c);
    }
    const groups = [...map.values()].map((g) => ({
      ...g,
      items: g.items.sort((a, b) => {
        // The section itself first, then its children by name.
        if (Number(a.id) === Number(g.root.id)) return -1;
        if (Number(b.id) === Number(g.root.id)) return 1;
        return String(a.name).localeCompare(String(b.name));
      }),
    }));
    groups.sort((a, b) => String(a.root.name).localeCompare(String(b.root.name)));
    return { groups, byId };
  }, [categories]);

  const q = query.trim().toLowerCase();
  const matches = (c, groupName) =>
    !q ||
    String(c.name).toLowerCase().includes(q) ||
    String(groupName).toLowerCase().includes(q);

  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((c) => matches(c, g.root.name)) }))
    .filter((g) => g.items.length);

  const emit = (next) => onChange([...next].map(Number));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(Number(id))) next.delete(Number(id));
    else next.add(Number(id));
    emit(next);
  };

  const setGroup = (items, on) => {
    const next = new Set(selected);
    for (const c of items) {
      if (on) next.add(Number(c.id));
      else next.delete(Number(c.id));
    }
    emit(next);
  };

  const clearAll = () => emit(new Set());
  const selectAllVisible = () => {
    const next = new Set(selected);
    for (const g of visible) for (const c of g.items) next.add(Number(c.id));
    emit(next);
  };

  const chosen = [...selected].map((id) => byId.get(id)).filter(Boolean);

  return (
    <div>
      {/* What is chosen, in one place, each one removable. */}
      <div
        style={{
          ...box,
          padding: "8px",
          minHeight: "44px",
          marginBottom: "8px",
          maxHeight: "110px",
          overflowY: "auto",
        }}
      >
        {chosen.length === 0 ? (
          <span style={{ color: "#888", fontSize: "13px" }}>
            No categories chosen yet
          </span>
        ) : (
          <>
            {chosen.map((c) => (
              <span
                key={c.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#eef4ff",
                  border: "1px solid #cfe0ff",
                  borderRadius: "12px",
                  padding: "1px 8px",
                  margin: "2px 4px 2px 0",
                  fontSize: "12px",
                }}
              >
                {c.name}
                <span
                  onClick={() => toggle(c.id)}
                  style={{ cursor: "pointer", color: "#666", fontWeight: 700 }}
                  title="Remove"
                >
                  &times;
                </span>
              </span>
            ))}
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="form-control login-input"
          placeholder="Search categories..."
          style={{ flex: 1 }}
        />
        <button
          type="button"
          onClick={selectAllVisible}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: "6px",
            fontSize: "12px",
            padding: "0 10px",
            whiteSpace: "nowrap",
          }}
        >
          {q ? "Add all shown" : "Add all"}
        </button>
        <button
          type="button"
          onClick={clearAll}
          style={{
            border: "1px solid #ced4da",
            background: "#fff",
            borderRadius: "6px",
            fontSize: "12px",
            padding: "0 10px",
          }}
        >
          Clear
        </button>
      </div>

      <div style={{ ...box, maxHeight: "300px", overflowY: "auto" }}>
        {visible.length === 0 && (
          <div style={{ padding: "12px", color: "#888", fontSize: "13px" }}>
            Nothing matches "{query}"
          </div>
        )}
        {visible.map((g) => {
          const on = g.items.filter((c) => selected.has(Number(c.id))).length;
          const isOpen = q ? true : !!open[g.root.id];
          return (
            <div key={g.root.id} style={{ borderBottom: "1px solid #eee" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  background: on ? "#f4f9ff" : "#fafafa",
                  cursor: "pointer",
                }}
                onClick={() => setOpen({ ...open, [g.root.id]: !open[g.root.id] })}
              >
                <span style={{ width: "12px", color: "#888", fontSize: "11px" }}>
                  {isOpen ? "▾" : "▸"}
                </span>
                <b style={{ flex: 1, fontSize: "14px" }}>{g.root.name}</b>
                <span style={{ color: "#888", fontSize: "12px" }}>
                  {on} of {g.items.length}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroup(g.items, on !== g.items.length);
                  }}
                  style={{
                    border: "1px solid #ced4da",
                    background: "#fff",
                    borderRadius: "5px",
                    fontSize: "11px",
                    padding: "1px 8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {on === g.items.length ? "None" : "All"}
                </button>
              </div>

              {isOpen && (
                <div style={{ padding: "4px 10px 8px 32px" }}>
                  {g.items.map((c) => (
                    <label
                      key={c.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        padding: "2px 0",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(Number(c.id))}
                        onChange={() => toggle(c.id)}
                      />
                      <span>
                        {c.name}
                        {Number(c.id) === Number(g.root.id) && (
                          <span style={{ color: "#888", fontSize: "11px" }}>
                            {" "}
                            (whole section)
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: "12px", color: "#666", marginTop: "6px" }}>
        {chosen.length} categor{chosen.length === 1 ? "y" : "ies"} chosen
      </div>
    </div>
  );
};

export default CategoryPicker;
